"use client";

/**
 * MultiArtworkUploader
 *
 * Shows multiple artwork images in a horizontal strip.
 * - First image is the thumbnail (marked with a "Thumbnail" badge).
 * - Drag-and-drop to reorder (HTML5 drag API, no external library).
 * - Add / remove images.
 * - Double-click any image to open a full-screen lightbox slideshow.
 * - Accepts data URLs or remote URLs; emits the current ordered array.
 */

import React, { useRef, useState, useEffect, useCallback } from "react";
import NextImage from "next/image";
import { X, GripVertical, ImagePlus, Star, ChevronLeft, ChevronRight, ZoomIn } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { isVideoUrl } from "@/lib/media-kind";

export interface MultiArtworkUploaderProps {
  /** Current ordered list of image URLs (data URLs or https) */
  images: string[];
  /** Called whenever the list changes (add / remove / reorder) */
  onChange: (images: string[]) => void;
  /** Max number of images allowed (default: 10) */
  maxImages?: number;
  className?: string;
  disabled?: boolean;
  /** Label for the add button */
  addLabel?: string;
  /** Also accept mp4/webm/mov video files alongside images & GIFs (default: false —
   *  square-artwork callers like bulk upload stay image-only). Items are still
   *  one flat string[]; video vs. image is detected per-URL via isVideoUrl(). */
  acceptVideo?: boolean;
}

export function MultiArtworkUploader({
  images,
  onChange,
  maxImages = 10,
  className,
  disabled = false,
  addLabel,
  acceptVideo = false,
}: MultiArtworkUploaderProps) {
  const resolvedAddLabel = addLabel ?? (acceptVideo ? "Add Image / GIF / Video" : "Add Image");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dragIndexRef = useRef<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  // ── Lightbox state ────────────────────────────────────────────────────────
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  const openLightbox = (index: number) => setLightboxIndex(index);
  const closeLightbox = useCallback(() => setLightboxIndex(null), []);

  const lightboxPrev = useCallback(() => {
    setLightboxIndex((i) => (i !== null && i > 0 ? i - 1 : images.length - 1));
  }, [images.length]);

  const lightboxNext = useCallback(() => {
    setLightboxIndex((i) => (i !== null && i < images.length - 1 ? i + 1 : 0));
  }, [images.length]);

  // Keyboard navigation for lightbox
  useEffect(() => {
    if (lightboxIndex === null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeLightbox();
      if (e.key === "ArrowLeft") lightboxPrev();
      if (e.key === "ArrowRight") lightboxNext();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [lightboxIndex, closeLightbox, lightboxPrev, lightboxNext]);

  // ── Add images (shared by file picker + drag-and-drop) ───────────────────────
  // Video clips are held as base64 data URLs in memory until save (same
  // FileReader path as images) — cap the raw file size so a multi-hundred-MB
  // clip can't hang the tab encoding it. Matches the Storage rules cap on
  // /profiles/{uid}/** and /artists/** (storage.rules) — keep them in sync.
  const MAX_VIDEO_BYTES = 50 * 1024 * 1024; // 50MB
  const addFiles = (fileList: File[]) => {
    let files = fileList.filter((f) => f.type.startsWith("image/") || (acceptVideo && f.type.startsWith("video/")));
    const oversized = files.filter((f) => f.type.startsWith("video/") && f.size > MAX_VIDEO_BYTES);
    if (oversized.length) {
      files = files.filter((f) => !oversized.includes(f));
      window.alert(`${oversized.length === 1 ? 'That video clip is' : `${oversized.length} of those video clips are`} over 50MB — trim it and try again.`);
    }
    if (!files.length) return;
    const readers = files.slice(0, maxImages - images.length).map(
      (file) =>
        new Promise<string>((resolve) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result as string);
          reader.readAsDataURL(file);
        })
    );
    Promise.all(readers).then((newUrls) => {
      onChange([...images, ...newUrls]);
    });
  };
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    addFiles(Array.from(e.target.files || []));
    e.target.value = ""; // reset so the same file can be re-added later
  };

  // ── Drop files to upload ─────────────────────────────────────────────────────
  const [isDropping, setIsDropping] = useState(false);
  const onFileDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDropping(false);
    addFiles(Array.from(e.dataTransfer.files || []));
  };
  const onFileDragOver = (e: React.DragEvent) => {
    if (e.dataTransfer.types?.includes("Files")) { e.preventDefault(); setIsDropping(true); }
  };

  // ── Remove ───────────────────────────────────────────────────────────────────
  const handleRemove = (index: number) => {
    const next = images.filter((_, i) => i !== index);
    onChange(next);
    // If the removed image was open in lightbox, close or shift
    if (lightboxIndex !== null) {
      if (next.length === 0) {
        closeLightbox();
      } else if (lightboxIndex >= next.length) {
        setLightboxIndex(next.length - 1);
      }
    }
  };

  // ── Drag-and-drop reorder ────────────────────────────────────────────────────
  const handleDragStart = (e: React.DragEvent, index: number) => {
    dragIndexRef.current = index;
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOverIndex(index);
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    const dragIndex = dragIndexRef.current;
    if (dragIndex === null || dragIndex === dropIndex) {
      setDragOverIndex(null);
      return;
    }
    const next = [...images];
    const [moved] = next.splice(dragIndex, 1);
    next.splice(dropIndex, 0, moved);
    onChange(next);
    dragIndexRef.current = null;
    setDragOverIndex(null);
  };

  const handleDragEnd = () => {
    dragIndexRef.current = null;
    setDragOverIndex(null);
  };

  return (
    <>
      <div className={cn("space-y-2", className)}>
        {/* Image strip */}
        {images.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {images.map((src, index) => (
              <div
                key={index}
                draggable={!disabled}
                onDragStart={(e) => handleDragStart(e, index)}
                onDragOver={(e) => handleDragOver(e, index)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, index)}
                onDragEnd={handleDragEnd}
                onDoubleClick={() => openLightbox(index)}
                className={cn(
                  "relative group flex-shrink-0 rounded-md overflow-hidden border-2 transition-all duration-150",
                  "cursor-grab active:cursor-grabbing",
                  index === 0
                    ? "border-primary shadow-sm shadow-primary/20"
                    : "border-border hover:border-primary/50",
                  dragOverIndex === index && dragIndexRef.current !== index
                    ? "border-primary/70 scale-105 shadow-lg"
                    : ""
                )}
                style={{ width: 72, height: 72 }}
                title="Double-click to view full size"
              >
                {isVideoUrl(src) ? (
                  <video
                    src={src}
                    muted
                    loop
                    playsInline
                    preload="metadata"
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                ) : (
                  <NextImage
                    src={src}
                    alt={`Artwork ${index + 1}`}
                    fill
                    className="object-cover"

                  />
                )}

                {/* Thumbnail badge */}
                {index === 0 && (
                  <div className="absolute top-0.5 left-0.5 z-10 flex items-center gap-0.5 bg-primary/90 text-primary-foreground rounded px-1 py-0.5">
                    <Star className="h-2.5 w-2.5" />
                    <span className="text-[9px] font-semibold leading-none">THUMB</span>
                  </div>
                )}

                {/* Double-click hint (visible on hover) */}
                <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity z-10 pointer-events-none">
                  <ZoomIn className="h-4 w-4 text-white/90" />
                </div>

                {/* Drag grip (visible on hover, below zoom icon) */}
                {!disabled && (
                  <div className="absolute inset-x-0 bottom-0 h-5 bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity z-20">
                    <GripVertical className="h-3 w-3 text-white/80" />
                  </div>
                )}

                {/* Remove button */}
                {!disabled && (
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); handleRemove(index); }}
                    className="absolute top-0.5 right-0.5 z-30 h-5 w-5 rounded-full bg-black/60 text-white flex items-center justify-center bb-hover-reveal transition-opacity hover:bg-destructive"
                    aria-label={`Remove image ${index + 1}`}
                  >
                    <X className="h-3 w-3" />
                  </button>
                )}

                {/* Index indicator for non-first images */}
                {index > 0 && (
                  <div className="absolute bottom-0.5 right-0.5 z-10 bg-black/60 text-white rounded px-1 py-0.5">
                    <span className="text-[9px] font-medium">{index + 1}</span>
                  </div>
                )}
              </div>
            ))}

            {/* Add button inline */}
            {images.length < maxImages && !disabled && (
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                onDragOver={onFileDragOver}
                onDragLeave={() => setIsDropping(false)}
                onDrop={onFileDrop}
                className={cn(
                  "flex-shrink-0 rounded-md border-2 border-dashed flex flex-col items-center justify-center gap-1 text-muted-foreground hover:text-primary transition-colors",
                  isDropping ? "border-primary bg-primary/10 text-primary" : "border-border hover:border-primary/50"
                )}
                style={{ width: 72, height: 72 }}
                aria-label={acceptVideo ? "Add or drop image, GIF or video" : "Add or drop image"}
                title={acceptVideo ? "Click to choose or drop image/GIF/video files here" : "Click to choose or drop image files here"}
              >
                <ImagePlus className="h-5 w-5" />
                <span className="text-[9px]">{isDropping ? "Drop" : "Add"}</span>
              </button>
            )}
          </div>
        )}

        {/* Empty state add / drop zone */}
        {images.length === 0 && !disabled && (
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            onDragOver={onFileDragOver}
            onDragLeave={() => setIsDropping(false)}
            onDrop={onFileDrop}
            className={cn(
              "w-full rounded-lg border-2 border-dashed flex flex-col items-center justify-center gap-1.5 py-6 text-xs text-muted-foreground hover:text-primary transition-colors",
              isDropping ? "border-primary bg-primary/10 text-primary" : "border-border hover:border-primary/50"
            )}
          >
            <ImagePlus className="h-5 w-5" />
            <span>{isDropping ? "Drop files to upload" : `${resolvedAddLabel} — or drag & drop`}</span>
          </button>
        )}

        {images.length > 0 && (
          <p className="text-[10px] text-muted-foreground">
            Drag to reorder · Double-click to preview · First item is the thumbnail · {images.length}/{maxImages}{acceptVideo ? " items" : " images"}
          </p>
        )}

        <input
          ref={fileInputRef}
          type="file"
          accept={acceptVideo ? "image/*,video/mp4,video/webm,video/quicktime" : "image/*"}
          multiple
          className="hidden"
          onChange={handleFileChange}
        />
      </div>

      {/* ── Lightbox overlay ───────────────────────────────────────────────── */}
      {lightboxIndex !== null && images[lightboxIndex] && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/95 backdrop-blur-sm"
          onClick={closeLightbox}
          role="dialog"
          aria-modal="true"
          aria-label="Artwork lightbox"
        >
          {/* Image container — stops click propagation so clicking the image doesn't close */}
          <div
            className="relative flex items-center justify-center w-full h-full max-w-5xl max-h-full px-16"
            onClick={(e) => e.stopPropagation()}
          >
            {isVideoUrl(images[lightboxIndex]) ? (
              <video
                src={images[lightboxIndex]}
                controls
                autoPlay
                playsInline
                className="max-w-full max-h-[85vh] object-contain rounded-lg shadow-2xl"
              />
            ) : (
              <img
                src={images[lightboxIndex]}
                alt={`Artwork ${lightboxIndex + 1}`}
                className="max-w-full max-h-[85vh] object-contain rounded-lg shadow-2xl select-none"
                draggable={false}
              />
            )}

            {/* Counter badge */}
            <div className="absolute top-4 left-1/2 -translate-x-1/2 bg-black/60 text-white text-xs rounded-full px-3 py-1">
              {lightboxIndex + 1} / {images.length}
              {lightboxIndex === 0 && (
                <span className="ml-2 text-primary font-semibold">★ Thumbnail</span>
              )}
            </div>
          </div>

          {/* Prev button */}
          {images.length > 1 && (
            <button
              type="button"
              className="absolute left-4 top-1/2 -translate-y-1/2 z-10 h-10 w-10 rounded-full bg-black/60 hover:bg-black/80 text-white flex items-center justify-center transition-colors"
              onClick={(e) => { e.stopPropagation(); lightboxPrev(); }}
              aria-label="Previous image"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
          )}

          {/* Next button */}
          {images.length > 1 && (
            <button
              type="button"
              className="absolute right-4 top-1/2 -translate-y-1/2 z-10 h-10 w-10 rounded-full bg-black/60 hover:bg-black/80 text-white flex items-center justify-center transition-colors"
              onClick={(e) => { e.stopPropagation(); lightboxNext(); }}
              aria-label="Next image"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          )}

          {/* Close button */}
          <button
            type="button"
            className="absolute top-4 right-4 z-10 h-9 w-9 rounded-full bg-black/60 hover:bg-black/80 text-white flex items-center justify-center transition-colors"
            onClick={closeLightbox}
            aria-label="Close lightbox"
          >
            <X className="h-4 w-4" />
          </button>

          {/* Thumbnail strip at bottom for multi-image navigation */}
          {images.length > 1 && (
            <div
              className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2 max-w-[80vw] overflow-x-auto pb-1"
              onClick={(e) => e.stopPropagation()}
            >
              {images.map((src, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setLightboxIndex(i)}
                  className={cn(
                    "flex-shrink-0 rounded overflow-hidden border-2 transition-all",
                    i === lightboxIndex ? "border-primary scale-110 shadow-lg" : "border-white/20 hover:border-white/60"
                  )}
                  style={{ width: 48, height: 48 }}
                  aria-label={`Go to item ${i + 1}`}
                >
                  {isVideoUrl(src) ? (
                    <video src={src} muted playsInline preload="metadata" className="w-full h-full object-cover" />
                  ) : (
                    <img src={src} alt="" className="w-full h-full object-cover" draggable={false} />
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </>
  );
}

export default MultiArtworkUploader;
